import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import useIsClinician from '../../../hooks/useIsClinician';
import useIsPatient from '../../../hooks/useIsPatient';
import { Button, Spin } from 'antd';
import { LoadingOutlined, LockFilled, UnlockFilled, CloseOutlined } from 'antd/icons';
import { debounce } from 'lodash';

interface ImagePreviewProps {
  lowResOriginal: any;
  crop: Crop;
  highResCrop: any;
  name: string;
  dimBackground: boolean;
}

const PLACEHOLDER_CROP: Crop = {
  x: -1,
  y: -1,
  width: 2,
  height: 2,
  unit: 'px'
};

const ImagePreview = ({ lowResOriginal, crop, highResCrop, name, dimBackground }: ImagePreviewProps) => {
  const { t } = useTranslation('common');
  const displayHighResCrop = () => {
    const image = new Image();
    image.src = highResCrop || lowResOriginal;
    const newWindow = window.open();

    if (newWindow) {
      newWindow.document.body.appendChild(image.cloneNode(true));
    }
  };

  return (
    <>
      <div style={{ marginTop: '2em' }}>
        <button
          style={{ padding: 0, border: 'none' }}
          type="button"
          data-testid="low-resolution-original"
          onClick={displayHighResCrop}
        >
          <ReactCrop className={dimBackground ? '' : 'no-dim-crop'} crop={crop} disabled onChange={() => {}}>
            <img src={lowResOriginal} alt={t('click to view region of interest')} />
          </ReactCrop>
        </button>
      </div>
      {name}
    </>
  );
};

const toDataURL = (file: File | Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const onDownloadCropClick = async (
  previewCanvas: HTMLImageElement | undefined,
  completedCrop: Crop
): Promise<string> => {
  if (!previewCanvas) {
    throw new Error('Crop canvas does not exist');
  }

  // This will size relative to the uploaded image size.
  // If you want to size according to what they are looking at on screen, remove scaleX + scaleY.
  // const scaleX = image.naturalWidth / image.width;
  // const scaleY = image.naturalHeight / image.height;

  const offscreen = new OffscreenCanvas(500, 500);
  const ctx = offscreen?.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  const { x, y, width, height } = completedCrop;
  ctx.drawImage(previewCanvas, x, y, width, height, 0, 0, offscreen.width, offscreen.height);
  // You might want { type: "image/jpeg", quality: <0 to 1> } to
  // reduce image size
  const blob = await offscreen.convertToBlob({
    type: 'image/png'
  });

  return toDataURL(blob);
};

const LockIndicator = ({ locked }: { locked: boolean }) => {
  const { t } = useTranslation('common');
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
        background: 'rgba(0, 0, 0, 0.5)',
        color: '#fff',
        width: '1.75em',
        height: '1.75em',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '0 0 4px 0'
      }}
    >
      {locked ? (
        <LockFilled aria-label={t('Region of interest locked')} />
      ) : (
        <UnlockFilled aria-label={t('Region of interest unlocked')} />
      )}
    </div>
  );
};

interface ImageWithCropProps {
  dataURL: string;
  crop: Crop;
  setCrop: (c: Crop) => void;
  setHiRes: (img: string) => void;
  onDelete: () => void;
  name: string;
  dimBackground: boolean;
}

// Basic way to check if current crop is equal placeholder crop
const matchesPlaceholderCrop = (crop: Crop): boolean =>
  crop.x === PLACEHOLDER_CROP.x &&
  crop.y === PLACEHOLDER_CROP.y &&
  crop.height === PLACEHOLDER_CROP.height &&
  crop.width === PLACEHOLDER_CROP.width;

// Basic way to check if crop container is missing (possible by making it too small)
const cropBoxMissing = (crop: Crop): boolean => !crop.x || !crop.y || !crop.width || !crop.height;

const getDefaultCropSizeFromImage = (img: HTMLImageElement): Crop => {
  const { width, height } = img;
  const side = Math.min(width / 2, height / 2);
  return {
    width: side,
    height: side,
    x: (width - side) / 2,
    y: (height - side) / 2,
    unit: 'px'
  };
};

const ImageWithCrop = ({ dataURL, crop, setCrop, setHiRes, name, onDelete, dimBackground }: ImageWithCropProps) => {
  const img = useRef<HTMLImageElement>(null);
  const [locked, setLocked] = useState<boolean>(false);
  const { t } = useTranslation('common');

  const loaded: React.ReactEventHandler<HTMLImageElement> = (e) => {
    // Crop areas load with default placeholder size, or crop can be missing altogether
    if (!crop.unit || matchesPlaceholderCrop(crop) || cropBoxMissing(crop)) {
      const updatedCrop = getDefaultCropSizeFromImage(img.current!); //NOSONAR
      setCrop(updatedCrop);
    }
  };

  // Ignore the pixelCrop arg for now
  const handleChangeCrop = (_: Crop, percentCrop: Crop) => {
    let updatedCrop = percentCrop;

    // Check if user has made crop area too small and reset it to being larger
    if (percentCrop.height < PLACEHOLDER_CROP.height || percentCrop.width < PLACEHOLDER_CROP.width) {
      updatedCrop = getDefaultCropSizeFromImage(img.current!); //NOSONAR
    }

    setCrop(updatedCrop);
  };

  return (
    <>
      <div style={{ marginTop: 20, position: 'relative' }}>
        <LockIndicator locked={locked} />

        <Button
          onClick={() => setLocked((l) => !l)}
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            zIndex: 1
          }}
          data-testid="lock-unlock-crop"
        >
          {locked ? t('Unlock') : t('Lock')}
        </Button>

        <ReactCrop
          className={dimBackground ? '' : 'no-dim-crop'}
          crop={crop}
          minWidth={25}
          minHeight={25}
          onChange={handleChangeCrop}
          onComplete={async (pixelCrop, percentCrop) => {
            if (img.current) {
              setHiRes(await onDownloadCropClick(img.current, pixelCrop));
              setCrop(percentCrop);
            }
          }}
          aspect={1}
          disabled={locked}
        >
          <img
            ref={img}
            style={{ maxWidth: '100%', height: 'auto' }}
            alt={t('please adjust the region of interest!')}
            src={dataURL}
            onLoad={loaded}
          />
        </ReactCrop>
      </div>
      <div style={{ display: 'flex' }}>
        <button
          type="button"
          data-testid="delete-image"
          onClick={onDelete}
          style={{
            verticalAlign: 'top',
            backgroundColor: 'red',
            marginLeft: 16,
            width: 20,
            height: 20,
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%'
          }}
          aria-label={t('Delete image')}
        >
          <CloseOutlined style={{ color: 'black' }} />
        </button>
        <span style={{ marginLeft: '4px' }}>{name}</span>
      </div>
    </>
  );
};

interface RegionOfInterest {
  crop: Crop;
  highRes?: string;
}

export interface CroppedImage extends RegionOfInterest {
  dataURL: string;
  name: string;
  uploadedToS3?: boolean;
  content?: string;
  s3Filename?: string;
}

const toCroppedImage = async (f: File): Promise<CroppedImage> => ({
  dataURL: await toDataURL(f),
  name: f.name,
  crop: {} as Crop
});

const toFiles = (fileList?: FileList | null): File[] => {
  const files = [] as File[];

  if (!fileList) return files;

  for (let i = 0; i < fileList.length; i++) {
    const file = fileList.item(i);

    if (file) {
      files.push(file);
    }
  }

  return files;
};

interface ImageCropProps {
  patient?: boolean;
  clinician?: boolean;
  images: CroppedImage[];
  onChange: (images: CroppedImage[], callback?: (updatedImages: CroppedImage[]) => void) => void;
  uploadLabel: string;
  dimBackground: boolean;
  downloadImage: (image: CroppedImage) => Promise<any>;
}

export const ImageCrop = ({
  patient,
  clinician,
  images,
  onChange,
  uploadLabel,
  dimBackground,
  downloadImage
}: ImageCropProps) => {
  const id = useId();
  // This state contains images that  are properly formatted for the component to use
  const [croppedImages, setCroppedImages] = useState<CroppedImage[]>([]);

  const stripHighRes = (images: CroppedImage[]): CroppedImage[] => images.map(({ highRes, ...rest }) => rest);

  // Used to store the raw images from page load, and when the user uploads a new one
  const [rawImages, setRawImages] = useState<CroppedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateRawImages = (updatedImages: CroppedImage[]) => {
    setRawImages(updatedImages);
    onChange(stripHighRes(updatedImages), imagesCroppedCallback);
  };

  const downloadAndSetImages = async (imagesToDownload: CroppedImage[]) => {
    const downloadedImages = await Promise.all(
      imagesToDownload.map(async (image) => {
        // inlineResponseImages will most likely catch the images and download them already
        // But just in case, we have the option to download the images here
        // And we would like to format them so that this component likes them
        if (image.content?.startsWith('data:')) {
          return { content: image.content, name: image.name };
        }
        const downloadedContent = await downloadImage(image);
        return { content: downloadedContent, name: image.name };
      })
    );

    // Adds the downloaded 'content' prop to the images under dataURL
    const imagesWithDataURL: CroppedImage[] = [];
    downloadedImages.forEach((downloadedImage) => {
      const indexOfImage = images.findIndex((image) => image.name === downloadedImage.name);

      const imageWithDataURL = {
        ...images[indexOfImage],
        dataURL: downloadedImage.content,
        content: images[indexOfImage].s3Filename,
        uploadedToS3: true
      };

      imagesWithDataURL.push(imageWithDataURL);
    });

    setCroppedImages(imagesWithDataURL);
    updateRawImages(imagesWithDataURL);
  };

  useEffect(() => {
    // When images updates, if they have 'content' but not dataURL
    // Then they need to be downloaded from S3
    const imagesToDownload = images.filter((image) => !!image.content && !image.dataURL);
    if (imagesToDownload.length) {
      downloadAndSetImages(imagesToDownload);
    } else if (images.length) {
      // If the dataURL is already present, just update state
      setRawImages(images);
      setCroppedImages(images);
    }
  }, [images]);

  const imagesCroppedCallback = (updatedImages: CroppedImage[]) => {
    setCroppedImages(updatedImages);
    setLoadingImages(false);
  };

  const filesChanged = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const latestUploads = await Promise.all(toFiles(e.target.files).map(toCroppedImage));
    // Upload new file -> format and set raw images -> this triggers the parent onChange handler
    // This uploads the image to S3, formats it, and updates the survey value & croppedImages
    if (latestUploads.length > 0) {
      setLoadingImages(true);
    }
    setRawImages((current) => {
      const newUploads = latestUploads
        .filter(({ dataURL }) => !current.find((c) => c.dataURL === dataURL))
        .map((newUL) => ({
          ...newUL,
          uploadedToS3: false,
          // Use placeholder crop values to prevent undefined errors
          crop: newUL.crop?.unit ? { ...newUL.crop } : { ...PLACEHOLDER_CROP }
        }));

      const newArray = [...current, ...newUploads];
      onChange?.(stripHighRes(newArray), imagesCroppedCallback);
      return newArray;
    });
  }, []);

  const debounceUpdateCrop = debounce((updatedCrops: CroppedImage[]) => onChange?.(updatedCrops), 300);

  const updateCropHandler = (newCrop: Crop, index: number) => {
    setCroppedImages((prevImages: CroppedImage[]): CroppedImage[] => {
      // Update the crop information
      // Debounce the survey update to prevent spamming
      const updatedCrops: CroppedImage[] = prevImages.map((img: CroppedImage, i: number): CroppedImage =>
        i === index ? { ...img, crop: newCrop } : img
      );

      // perform debounceUpdateCrop in the setter so it has access to the same object, otherwise it will reference
      // the old version and mismatch will occur
      debounceUpdateCrop(stripHighRes(updatedCrops));
      return updatedCrops;
    });
  };

  // TODO: Check this works as expected - may need the same treatment as the initial upload stuff
  const updateHiResHandler = (newHiRes: string, index: number) => {
    // Update the 'high res' information aka the actual cropped image.
    // Debounce the survey update to prevent spamming
    setCroppedImages((prevImages: CroppedImage[]): CroppedImage[] => {
      const updatedCrops: CroppedImage[] = prevImages.map((img: CroppedImage, i: number): CroppedImage =>
        i === index ? { ...img, highRes: newHiRes } : img
      );

      // perform debounceUpdateCrop in the setter so it has access to the same object, otherwise it will reference
      // the old version and mismatch will occur
      debounceUpdateCrop(stripHighRes(updatedCrops));
      return updatedCrops;
    });
  };

  const labelKeyDownHandler = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  return (
    <div>
      {!patient && !clinician && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label
            tabIndex={0}
            onKeyDown={labelKeyDownHandler}
            // onClick not needed as it is handled automatically
            role="button"
            htmlFor={`image-input-${id}`}
            className="sd-btn"
            style={{ borderRadius: 0, marginInline: 'auto', marginBottom: '2em' }}
          >
            <span>{uploadLabel}</span>
            <input
              ref={fileInputRef}
              tabIndex={-1}
              id={`image-input-${id}`}
              type="file"
              onChange={filesChanged}
              accept="image/*"
              multiple
              style={{
                position: 'absolute',
                opacity: 0,
                right: 0,
                top: 0
              }}
              disabled
            />
          </label>
        </div>
      )}

      {patient && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label
            tabIndex={0}
            onKeyDown={labelKeyDownHandler}
            // onClick not needed as it is handled automatically
            role="button"
            htmlFor={`image-input-${id}`}
            className="sd-btn"
            style={{ borderRadius: 0, marginInline: 'auto', marginBottom: '2em' }}
          >
            <div style={{ width: '150px' }}>
              {loadingImages ? <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} /> : uploadLabel}
            </div>
            <input
              ref={fileInputRef}
              id={`image-input-${id}`}
              type="file"
              onChange={filesChanged}
              accept="image/*"
              multiple
              style={{
                position: 'absolute',
                opacity: 0,
                right: 0,
                top: 0
              }}
              disabled={loadingImages}
            />
          </label>
          {croppedImages.map(({ dataURL, crop, name }, index) => (
            <ImageWithCrop
              key={`${name}-${index}`}
              dataURL={dataURL}
              crop={crop}
              name={name}
              setCrop={(c: Crop) => updateCropHandler(c, index)}
              setHiRes={(s: string) => updateHiResHandler(s, index)}
              onDelete={() => {
                setCroppedImages((crops) => [...crops.slice(0, index), ...crops.slice(index + 1)]);

                const newImagesArray = [...rawImages.slice(0, index), ...rawImages.slice(index + 1)];
                updateRawImages(newImagesArray);
              }}
              dimBackground={dimBackground}
            />
          ))}
        </div>
      )}

      {patient && clinician && <hr />}

      {clinician &&
        croppedImages.map(({ dataURL, crop, highRes, name }, index) => (
          <ImagePreview
            key={`${name}-${index}`}
            crop={crop}
            lowResOriginal={dataURL}
            highResCrop={highRes}
            name={name}
            dimBackground={false}
          />
        ))}
    </div>
  );
};

interface ImageCropForUserProps {
  images: CroppedImage[];
  onChange: (images: CroppedImage[]) => void;
  uploadLabel?: string;
  dimBackground?: boolean;
  downloadImage: (image: CroppedImage) => Promise<any>;
}

export const ImageCropForUser = ({
  onChange,
  images,
  uploadLabel = 'Choose Images',
  dimBackground = true,
  downloadImage
}: ImageCropForUserProps) => (
  <ImageCrop
    clinician={useIsClinician()}
    patient={useIsPatient()}
    images={images}
    onChange={onChange}
    uploadLabel={uploadLabel}
    dimBackground={dimBackground}
    downloadImage={downloadImage}
  />
);

export default ImageCrop;
